/**
 * @fileoverview "Confirm Payment" prompt for a prospect's open target.
 * @description Confirming that the initial payment was received completes the target and turns
 * the prospect into an Active Client (Super Admin only — see resolveTenantActions). Split out of
 * TenantDetailModal so the same prompt can also be opened on its own, from a table row's actions
 * menu (see TenantActionModal).
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useTranslation } from 'react-i18next'

import { useConfirmTargetPayment } from '@/features/sales/hooks/useConfirmTargetPayment'

import DialogActions from './DialogActions'

interface ConfirmPaymentPanelProps {
  tenantId: string
  targetId: string
  confirmedByUserId: string
  onDone: () => void
  onCancel: () => void
}

/**
 * @component
 * @param {ConfirmPaymentPanelProps} props - The target to confirm, who confirms it, and handlers.
 * @param {string} props.tenantId - The prospect the target belongs to.
 * @param {string} props.targetId - The open target being paid for.
 * @param {string} props.confirmedByUserId - The signed-in user, recorded as confirming it.
 * @param {() => void} props.onDone - Called once the payment is confirmed.
 * @param {() => void} props.onCancel - Called when the prompt is cancelled.
 */
function ConfirmPaymentPanel({
  tenantId,
  targetId,
  confirmedByUserId,
  onDone,
  onCancel,
}: ConfirmPaymentPanelProps) {
  const { t } = useTranslation()
  const confirmPayment = useConfirmTargetPayment(tenantId)

  return (
    <div
      className="flex flex-col gap-2.5 rounded-lg border border-row-border bg-surface-hover p-3.5"
      data-testid="confirm-payment-panel"
    >
      <p className="text-[13px] text-ink" data-testid="confirm-payment-message">
        {t('sales.tenantDetail.payment.confirmPrompt')}
      </p>
      <DialogActions
        testIdPrefix="confirm-payment"
        cancelLabel={t('sales.tenantDetail.payment.cancel')}
        confirmLabel={t('sales.tenantDetail.payment.confirm')}
        onCancel={onCancel}
        onConfirm={() =>
          confirmPayment.mutate({ targetId, confirmedByUserId }, { onSuccess: onDone })
        }
        isLoading={confirmPayment.isPending}
        disabled={false}
      />
    </div>
  )
}

export default ConfirmPaymentPanel
